import authApi from "../api/authApi";
import { setToken } from "../utils/token";
import { setTenantId, getDefaultTenantId } from "../utils/tenant";
import { pickProfileImageUrl } from "../utils/profileImage";

const CURRENT_USER_KEY = "currentUser";

function resolveTenantId() {
	const tenantId = getDefaultTenantId();
	if (tenantId) setTenantId(tenantId);
	return tenantId;
}

// Exchange Google credential (ID token) for app session
export async function loginWithGoogleCredential(credential, mfaCode) {
	try {
		const attempts = [
			{ url: "/google", body: { credential } },
			{ url: "/google/login", body: { credential } },
			{ url: "/login/google", body: { credential } },
			{ url: "/social/google", body: { credential } },
			{ url: "/oauth/google", body: { id_token: credential } },
		];
		const normalizedMfa = String(mfaCode || "").trim();
		let resp;
		let lastErr;
		for (const a of attempts) {
			try {
				const body = { ...(a.body || {}) };
				if (normalizedMfa) body.mfa_code = normalizedMfa;
				resp = await authApi.post(a.url, body, { params: { check_mfa: true } });
				break;
			} catch (e) {
				const status = e?.response?.status;
				const backendMsg = e?.response?.data?.message || e?.message || "";
				const explicitMfa =
					e?.code === "MFA_REQUIRED" ||
					e?.response?.data?.code === "MFA_REQUIRED" ||
					e?.response?.data?.mfa_required === true ||
					(e?.response?.headers && String(e.response.headers["x-mfa-required"]).toLowerCase() === "true") ||
					((status === 400 || status === 401 || status === 403) && /mfa/i.test(backendMsg) && /(required|verify|code)/i.test(backendMsg));
				if (explicitMfa) {
					const err = new Error("Multi-Factor Authentication (MFA) is required");
					err.code = "MFA_REQUIRED";
					err.response = e?.response || { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
					throw err;
				}
				lastErr = e;
				continue;
			}
		}
		if (!resp) throw lastErr || new Error("Google login failed");
		const data = resp?.data || {};
		try {
			const tenantFromHeader =
				resp?.headers?.["x-tenant-id"] || resp?.headers?.["X-Tenant-ID"];
			const tenantFromBody = data?.tenant_id || data?.tenantId;
			const resolvedTenant = String(tenantFromHeader || tenantFromBody || "").trim();
			if (resolvedTenant) setTenantId(resolvedTenant);
		} catch {}
		const token = data?.access_token || data?.accessToken || data?.token || data?.jwt;
		if (token) setToken(token);

		if (!token) {
			const explicitMfaRequired =
				data?.mfa_required === true ||
				data?.require_mfa === true ||
				data?.requires_mfa === true;
			if (explicitMfaRequired) {
				const err = new Error("Multi-Factor Authentication (MFA) is required");
				err.code = "MFA_REQUIRED";
				err.response = { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
				throw err;
			}
		}

		try {
			if (data?.user && typeof data.user === "object") {
				const u = { ...data.user };
				const img = pickProfileImageUrl(u);
				if (img) {
					if (!u.profile_picture && !u.profilePicture) u.profile_picture = img;
					if (!u.image) u.image = img;
				}
				localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(u));
			}
		} catch {}
		return data;
	} catch (e) {
		const status = e?.response?.status;
		const backendMsg = e?.response?.data?.message || e?.message || "";
		const explicitMfa =
			e?.code === "MFA_REQUIRED" ||
			e?.response?.data?.code === "MFA_REQUIRED" ||
			e?.response?.data?.mfa_required === true ||
			(e?.response?.headers && String(e.response.headers["x-mfa-required"]).toLowerCase() === "true") ||
			((status === 400 || status === 401 || status === 403) && /mfa/i.test(backendMsg) && /(required|verify|code)/i.test(backendMsg));
		if (explicitMfa && e?.code !== "MFA_REQUIRED") {
			const err = new Error("Multi-Factor Authentication (MFA) is required");
			err.code = "MFA_REQUIRED";
			err.response = e?.response || { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
			throw err;
		}
		throw e;
	}
}

export async function authenticateWithRegister(email, password) {
	const tenantId = resolveTenantId();
	const payload = { email, password, ...(tenantId ? { tenant_id: tenantId } : {}) };
	const { data } = await authApi.post("/register", payload);
	if (data?.token) {
		setToken(data.token);
	}
	return data;
}

export async function loginWithCredentials(email, password, mfaCode) {
	const normalizedEmail = String(email || "").trim();
	const tenantId = resolveTenantId();
	if (!tenantId) {
		const err = new Error("Tenant ID is not configured. Set REACT_APP_DEFAULT_TENANT_ID.");
		err.code = "TENANT_NOT_FOUND";
		throw err;
	}

	// Swagger LoginRequest: { email, password, tenant_id }
	const payload = {
		email: normalizedEmail,
		password,
		tenant_id: tenantId
	};
	if (mfaCode) payload.mfa_code = String(mfaCode).trim();

	let resp;
	try {
		resp = await authApi.post("/login", payload, {
			headers: { "X-Tenant-ID": tenantId },
			params: { check_mfa: true }
		});
	} catch (e) {
		const status = e?.response?.status;
		const backendMsg = String(e?.response?.data?.message || e?.response?.data?.detail || e?.message || "");
		const explicitMfa =
			e?.response?.data?.code === "MFA_REQUIRED" ||
			e?.response?.data?.mfa_required === true ||
			((status === 400 || status === 401 || status === 403) &&
				/mfa|totp|2fa|two.?factor/i.test(backendMsg) &&
				/(required|verify|code|invalid)/i.test(backendMsg));
		if (explicitMfa) {
			const err = new Error("Multi-Factor Authentication (MFA) is required");
			err.code = "MFA_REQUIRED";
			err.response = e?.response || {
				status: 403,
				data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true }
			};
			throw err;
		}
		throw e;
	}
	const data = resp?.data || {};
	try {
		const tenantFromHeader =
			resp?.headers?.["x-tenant-id"] ||
			resp?.headers?.["X-Tenant-ID"];
		const tenantFromBody = data?.tenant_id || data?.tenantId;
		const resolvedTenant = String(tenantFromHeader || tenantFromBody || tenantId || "").trim();
		if (resolvedTenant) setTenantId(resolvedTenant);
	} catch {}

	const tokenInPayload = data?.access_token || data?.accessToken || data?.token || data?.jwt;
	if (!tokenInPayload) {
		const explicitMfaRequired =
			data?.mfa_required === true ||
			data?.require_mfa === true ||
			data?.requires_mfa === true;
		if (explicitMfaRequired) {
			const err = new Error("Multi-Factor Authentication (MFA) is required");
			err.code = "MFA_REQUIRED";
			err.response = { status: 403, data: { message: err.message, code: "MFA_REQUIRED", mfa_required: true } };
			throw err;
		}
	}
	const token = tokenInPayload;
	if (token) {
		setToken(token);
	}
	try {
		if (data?.user && typeof data.user === "object") {
			const u = { ...data.user };
			const img = pickProfileImageUrl(u);
			if (img) {
				if (!u.profile_picture && !u.profilePicture) u.profile_picture = img;
				if (!u.image) u.image = img;
			}
			localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(u));
		}
	} catch {}
	try {
		const rolesFromRoot = Array.isArray(data?.roles) ? data.roles : null;
		const rolesFromUser = Array.isArray(data?.user?.roles) ? data.user.roles : null;
		const role =
			(data?.role ||
			 data?.user?.role ||
			 (rolesFromRoot && rolesFromRoot.length ? rolesFromRoot[0] : "") ||
			 (rolesFromUser && rolesFromUser.length ? rolesFromUser[0] : "") ||
			 (data?.user?.is_admin ? "admin" : "") ||
			 "").toString().toLowerCase();
		if (role) localStorage.setItem("role", role);
	} catch {}
	return data;
}

export async function sendVerificationCode(email) {
	const { data } = await authApi.post("/send-verification-code", { email });
	return data;
}

export async function verifyEmailCode(email, code) {
	const { data } = await authApi.post("/verify-code", { email, code });
	const tokenFromApi = data?.verification_token || data?.verificationToken || data?.token;
	if (tokenFromApi) {
		try { localStorage.setItem("verificationToken", tokenFromApi); } catch (_) {}
	}
	return data;
}

export async function registerUser({ email, name, password, verificationToken }) {
	const tenantId = resolveTenantId();
	if (!tenantId) {
		throw new Error("Tenant ID is not configured. Set REACT_APP_DEFAULT_TENANT_ID.");
	}
	const payload = {
		email,
		name,
		password,
		tenant_id: tenantId
	};
	const { data } = await authApi.post("/register", payload);
	return data;
}

export async function requestPasswordReset(email) {
	const tenantId = resolveTenantId();
	const payload = { email, ...(tenantId ? { tenant_id: tenantId } : {}) };
	const { data } = await authApi.post("/forgot-password", payload);
	return data;
}

export async function verifyResetCode(email, code) {
	const normalizedCode = String(code || "").trim();
	const numericCode = /^\d+$/.test(normalizedCode) ? Number(normalizedCode) : normalizedCode;
	const attempts = [
		{ email, code: normalizedCode },
		{ email, code: numericCode },
		{ email, verification_code: normalizedCode },
		{ email, verification_code: numericCode },
		{ code: normalizedCode, email },
		{ verification_code: normalizedCode, email },
		{ email, code: normalizedCode, purpose: "password_reset" },
		{ email, verification_code: normalizedCode, purpose: "password_reset" },
		{ email, reset_code: normalizedCode },
		{ email, otp: normalizedCode },
		{ email, one_time_code: normalizedCode },
		{ email, channel: "email", code: normalizedCode },
	];
	let lastErr;
	for (const body of attempts) {
		try {
			const { data } = await authApi.post("/verify-code", body);
			const tokenFromApi = data?.verification_token || data?.verificationToken || data?.token;
			if (tokenFromApi) {
				try { localStorage.setItem("passwordResetToken", tokenFromApi); } catch (_) {}
			}
			return data;
		} catch (e) {
			lastErr = e;
			const status = e?.response?.status;
			if (status !== 400 && status !== 404 && status !== 422) break;
		}
	}
	throw lastErr;
}

export async function resetPassword(email, code, new_password) {
	const tenantId = resolveTenantId();
	const payload = {
		email,
		code: String(code || "").trim(),
		new_password,
		...(tenantId ? { tenant_id: tenantId } : {})
	};
	const { data } = await authApi.post("/reset-password", payload);
	return data;
}
